import { useMemo, useState } from 'react'
import ControlPanel from './components/ControlPanel'
import ContentLibrary from './components/ContentLibrary'
import ArticleListPanel from './components/ArticleListPanel'
import PreviewDOM from './components/PreviewDOM'
import TemplateGallery from './components/TemplateGallery'
import { CONTENT_LIBRARY } from './data/contentLibrary'
import { COPYBOOK_TEMPLATES } from './data/copybookTemplates'
import { useCopybookStore } from './store/copybookStore'

function App() {
  const [showSettings, setShowSettings] = useState(false)
  const [mobileTab, setMobileTab] = useState<'edit' | 'preview'>('edit')
  const [isContentEditorCollapsed, setIsContentEditorCollapsed] = useState(true)
  const [isContentLibraryCollapsed, setIsContentLibraryCollapsed] = useState(false)
  const [showArticleList, setShowArticleList] = useState(false)
  const store = useCopybookStore()
  const templates = COPYBOOK_TEMPLATES

  const activeTemplate = useMemo(
    () => templates.find((template) => template.id === store.activeTemplateId) ?? null,
    [store.activeTemplateId, templates]
  )

  return (
    <div className="min-h-screen print:min-h-0 print:h-auto bg-[linear-gradient(180deg,#f8fafc_0%,#eef2ff_100%)] print:bg-none print:bg-white text-gray-900">
      <header className="border-b border-gray-200 bg-white/90 backdrop-blur print:hidden">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4 pl-3 pr-4 py-2 lg:pl-4 lg:pr-6">
          <div>
            <h1 className="text-xl lg:text-2xl font-semibold tracking-tight">字帖產生器</h1>
            <p className="mt-0.5 text-xs lg:text-sm text-gray-600">先選樣式，再選內容，全部預設為 A4。</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs lg:text-sm font-medium text-blue-700">A4 預設</span>
            <button
              type="button"
              onClick={() => setShowArticleList(true)}
              className="rounded-full border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs lg:text-sm font-medium text-blue-700 hover:bg-blue-100"
            >
              文章列表
            </button>
            <a
              href="/user-guide.html"
              className="rounded-full border border-gray-300 bg-white px-3 py-1.5 lg:px-4 lg:py-2 text-xs lg:text-sm font-medium text-gray-700 hover:border-gray-400"
            >
              使用說明
            </a>
            <button
              onClick={() => setShowSettings((v) => !v)}
              className="rounded-full border border-gray-300 bg-white px-3 py-1.5 lg:px-4 lg:py-2 text-xs lg:text-sm font-medium text-gray-700 hover:border-gray-400"
            >
              {showSettings ? '關閉設定' : '進階設定'}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] pl-3 pr-4 pt-3 pb-6 lg:pl-4 lg:pr-6 print:p-0 print:m-0 print:max-w-none">
        {!store.activeTemplateId ? (
          <div className="mx-auto mt-5 max-w-4xl rounded-3xl border border-white/50 bg-white/70 p-8 shadow-lg backdrop-blur-md print:hidden">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-semibold text-gray-900">1. 選擇字帖樣式</h2>
              <p className="mt-2 text-sm text-gray-600">請先選擇您想要練習的字帖風格，之後可進行細節設定。</p>
            </div>
            <TemplateGallery
              templates={templates}
              activeTemplateId={store.activeTemplateId}
              onSelect={(template) => {
                store.applyTemplate(template)
                setShowSettings(true)
              }}
            />
          </div>
        ) : (
          <div className="space-y-4 print:space-y-0">
            {/* 行動端標籤切換列 */}
            <div className="flex lg:hidden print:hidden bg-gray-200/60 backdrop-blur rounded-2xl p-1 border border-gray-300/50 shadow-inner w-full max-w-sm mx-auto mb-3">
              <button
                type="button"
                onClick={() => setMobileTab('edit')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-center text-lg font-bold rounded-xl transition-all duration-300 ${mobileTab === 'edit'
                  ? 'bg-blue-600 text-white shadow-md transform scale-[1.02]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/40'
                  }`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
                </svg>
                編輯設定
              </button>
              <button
                type="button"
                onClick={() => setMobileTab('preview')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-center text-lg font-bold rounded-xl transition-all duration-300 ${mobileTab === 'preview'
                  ? 'bg-emerald-600 text-white shadow-md transform scale-[1.02]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/40'
                  }`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                </svg>
                字帖預覽
              </button>
            </div>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,400px)_minmax(0,1fr)] print:block print:gap-0">
              <section className={`w-full max-w-[400px] min-w-0 space-y-6 print:hidden ${mobileTab === 'edit' ? 'block' : 'hidden lg:block'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <button
                    onClick={() => store.clearTemplateSelection()}
                    className="flex items-center justify-center gap-2 rounded-full border border-gray-300 bg-white/70 px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-400 backdrop-blur-sm shadow-sm w-full sm:w-auto"
                  >
                    ← 返回重選字帖樣式
                  </button>
                  <span className="text-sm font-medium text-gray-500 text-center sm:text-right">
                    當前樣式：{activeTemplate?.name}
                  </span>
                </div>

                <div className={`rounded-3xl border border-white/50 bg-white/50 px-3 shadow-lg backdrop-blur-md ${isContentEditorCollapsed ? 'py-1' : 'py-2.5'}`}>
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-bold text-gray-900">目前內容</h2>
                      <span className="text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200/60 px-2 py-0.5 rounded-full font-mono">
                        {store.text.trim().length} 字
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {!isContentEditorCollapsed && (
                        <>
                          <label className="flex items-center gap-1 text-[11px] text-gray-700 bg-white/80 border border-gray-200/80 px-2 py-0.5 rounded-full cursor-pointer select-none shadow-sm hover:bg-white mr-0.5" title="文字換行時，字帖格子自動跳至下一列／行開頭">
                            <input
                              type="checkbox"
                              checked={store.preserveLineBreaks}
                              onChange={(e) => store.setPreserveLineBreaks(e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                            />
                            <span className="font-semibold text-blue-800">換行跳格</span>
                          </label>
                          <label className="flex items-center gap-1 text-[11px] text-gray-600 cursor-pointer select-none mr-1" title="點擊「去標點」時是否一併清除換行符號">
                            <input
                              type="checkbox"
                              checked={store.filterNewlinesOnClean}
                              onChange={(e) => store.setFilterNewlinesOnClean(e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                            />
                            <span>過濾跳行</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              if (store.filterNewlinesOnClean) {
                                // 移除標點符號、特殊符號與所有空白換行
                                const cleaned = store.text.replace(/[\s\p{P}\p{S}]/gu, '')
                                store.setText(cleaned)
                              } else {
                                // 僅移除標點符號與行內空格，保留換行
                                const cleaned = store.text
                                  .replace(/[^\S\r\n\u3000]|\p{P}|\p{S}/gu, '')
                                  .replace(/\n{3,}/g, '\n\n')
                                store.setText(cleaned)
                              }
                            }}
                            className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 shadow-sm"
                            title="過濾標點符號（可勾選是否過濾跳行）"
                          >
                            去標點
                          </button>
                          <button
                            type="button"
                            onClick={() => store.setText('')}
                            className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-rose-600 border border-rose-100 hover:bg-rose-50 shadow-sm"
                            title="清空文字"
                          >
                            清空
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => setIsContentEditorCollapsed((v) => !v)}
                        className="shrink-0 rounded-full border border-gray-300 bg-white/70 px-2.5 py-1 text-xs font-medium text-gray-700 shadow-sm hover:border-gray-400"
                        aria-expanded={!isContentEditorCollapsed}
                        aria-controls="current-content-editor"
                      >
                        {isContentEditorCollapsed ? '展開' : '收起'}
                      </button>
                    </div>
                  </div>
                  {isContentEditorCollapsed ? (
                    <button
                      type="button"
                      onClick={() => setIsContentEditorCollapsed(false)}
                      className="mt-1 w-full truncate rounded-2xl border border-gray-200 bg-white/50 px-3 py-1.5 text-left text-xs text-gray-600 hover:border-gray-300"
                    >
                      {store.text.trim() || '尚未輸入內容（點擊展開編輯）'}
                    </button>
                  ) : (
                    <textarea
                      id="current-content-editor"
                      value={store.text}
                      onChange={(e) => {
                        store.setContentSource('custom')
                        store.setActiveContentId(null)
                        store.setText(e.target.value)
                      }}
                      placeholder="請輸入要做成字帖的內容..."
                      className="h-32 w-full rounded-2xl border border-gray-200 bg-white/70 p-3 text-sm leading-6 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-sans"
                    />
                  )}
                </div>

                <div className={`rounded-3xl border border-white/50 bg-white/70 px-1.5 shadow-lg backdrop-blur-md ${isContentLibraryCollapsed ? 'py-0' : 'py-0.5'}`}>
                  <div className={isContentLibraryCollapsed ? 'flex items-center justify-between gap-3' : 'mb-4 flex items-start justify-between gap-3'}>
                    <div>
                      {isContentLibraryCollapsed ? (
                        <h2 className="text-sm font-semibold text-gray-900">選擇內容</h2>
                      ) : (
                        <>
                          <h2 className="text-lg font-semibold">2. 選擇內容</h2>
                          <p className="mt-1 text-sm text-gray-600">可直接套用詩詞、文章。</p>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsContentLibraryCollapsed((v) => !v)}
                      className="shrink-0 rounded-full border border-gray-300 bg-white/70 px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:border-gray-400"
                      aria-expanded={!isContentLibraryCollapsed}
                      aria-controls="content-library-panel"
                    >
                      {isContentLibraryCollapsed ? '展開' : '收起'}
                    </button>
                  </div>
                  {isContentLibraryCollapsed ? (
                    <button
                      type="button"
                      onClick={() => setIsContentLibraryCollapsed(false)}
                      className="mt-1 w-full truncate rounded-2xl border border-gray-200 bg-white/50 px-3 py-1 text-left text-xs text-gray-600 hover:border-gray-300"
                    >
                      目前內容：{store.title || store.text.trim() || '尚未選擇內容'}
                    </button>
                  ) : (
                    <div id="content-library-panel">
                      <ContentLibrary
                        items={CONTENT_LIBRARY}
                        userItems={store.userItems}
                        activeContentId={store.activeContentId}
                        contentSource={store.contentSource}
                        customText={store.text}
                        onChangeSource={(source) => {
                          store.setContentSource(source)
                          store.setActiveContentId(null)
                          if (source !== 'custom') {
                            const first = CONTENT_LIBRARY.find((item) => item.category === source) ?? null
                            if (first) {
                              store.setText(first.content)
                              store.setTitle(first.title)
                              store.setSubtitle(first.source)
                              store.setActiveContentId(first.id)
                            }
                          }
                        }}
                        onPick={(item) => {
                          store.setContentSource(item.category)
                          store.setActiveContentId(item.id)
                          store.setText(item.content)
                          store.setTitle(item.title)
                          store.setSubtitle(item.source)
                        }}
                        onChangeCustomText={(text) => {
                          store.setContentSource('custom')
                          store.setActiveContentId(null)
                          store.setText(text)
                        }}
                        onAddItem={(item) => store.addUserItem(item)}
                        onUpdateItem={(id, updates) => store.updateUserItem(id, updates)}
                        onDeleteItem={(id) => store.deleteUserItem(id)}
                      />
                    </div>
                  )}
                </div>

                {/* 3. 細節設定：提供使用者微調格子大小、字體顏色等進階參數的控制面板 */}
                <div className="rounded-3xl border border-gray-50/20 bg-white/50 shadow-lg backdrop-blur-md overflow-hidden">
                  <div className="border-b border-gray-100/20 px-5 py-4">
                    <h2 className="text-lg font-semibold">3. 細節設定</h2>
                    <p className="mt-1 text-sm text-gray-600">微調格子大小、字體顏色等細節。</p>
                  </div>
                  <ControlPanel />
                </div>
              </section>

              <aside className={`min-w-0 space-y-6 print:space-y-0 print:block print:w-full print:p-0 print:m-0 ${mobileTab === 'preview' ? 'block' : 'hidden lg:block'}`}>
                <div className="rounded-3xl border border-white/50 bg-white/70 p-4 shadow-lg backdrop-blur-md print:border-none print:bg-transparent print:shadow-none print:p-0">
                  <div className="mb-3 flex items-center justify-between print:hidden">
                    <div>
                      <h2 className="text-lg font-semibold">A4 預覽</h2>
                      <p className="text-sm text-gray-600">即時反映樣式與內容。</p>
                    </div>
                  </div>
                  <PreviewDOM />
                </div>


              </aside>
            </div>
          </div>
        )}
      </main>
      {showArticleList && (
        <ArticleListPanel
          items={CONTENT_LIBRARY}
          userItems={store.userItems}
          activeContentId={store.activeContentId}
          onClose={() => setShowArticleList(false)}
          onPick={(item) => {
            store.setContentSource(item.category)
            store.setActiveContentId(item.id)
            store.setText(item.content)
            store.setTitle(item.title)
            store.setSubtitle(item.source)
            setShowArticleList(false)
          }}
        />
      )}
    </div>
  )
}

export default App
